// bluaIQ — M12: Subscription & Billing Module
// Public plans (from src/shared/plans.ts): Starter $199 | Pro $299 | Growth $499
// (Growth = internal BUILDER key). Founding = invite-only; Enterprise = contact-only.
// Features: plan comparison, FAQ, Enterprise form, pause, referrals
// Users per plan: 1 | 3 | 6 (must stay in sync with LandingPage.tsx pricing cards
// and PLAN_LIMITS.maxUsers in src/server/index.ts)

import { apiFetch } from '../utils/api'
import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { isDemoProject } from '../utils/demoData'
import { getBillingPlans, getPlanRank } from '../shared/plans'
import SavedProjectOnlyNotice from './SavedProjectOnlyNotice'
import { useTranslation } from 'react-i18next'
import { loadStripe } from '@stripe/stripe-js'
import {
  Crown, Zap, Gift, Plus, Check, Star, Pause, Play, X,
  ChevronDown, ChevronUp, HelpCircle, Building2, Send, Lock,
  Users, HardDrive, FolderOpen, MessageSquare, Sparkles,
  Download, CreditCard, ChevronLeft
} from 'lucide-react'

const stripePromise = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
  : null

// ── TYPES ─────────────────────────────────────────

interface Subscription {
  plan: string
  // PR-NEW: server-authoritative fields the /api/subscription
  // response has carried since PR #95 — now typed so the UI can
  // read the canonical "Current Plan" value (effectivePlan,
  // computed via deriveEffectivePlan on the server). The bug
  // before this PR was that the Membership UI read `plan.plan`
  // (legacy raw column) while the diagnose panel read
  // `diagnose.user.effectivePlan` (server-derived). When the
  // two diverged the top card + comparison table fell behind
  // diagnose, producing "diagnose says BUILDER, card says STARTER".
  currentPlan?: string
  effectivePlan?: string
  subscriptionStatus?: string
  stripeCustomerLinked?: boolean
  stripeSubscriptionLinked?: boolean
  // PR-1: the user's REAL current Stripe price (null when no linked sub / Stripe
  // unavailable). Plan & Billing prefers this over the catalog price.
  currentStripePriceId?: string | null
  currentPriceMonthly?: number | null
  currentPriceYearly?: number | null
  currency?: string | null
  currentBillingInterval?: 'month' | 'year' | null
  limits: { maxProjects: number; aiCallsPerMonth: number; priceMonthly: number; storageGB: number; extraProjectPrice: number }
  paused: boolean
  projects: { current: number; includedInPlan: number; extraPurchased: number; max: number; extraProjectPrice: number }
  aiUsage: { used: number; max: number }
  trial: { active: boolean; endsAt: string }
  referrals: { count: number; freeMonthsEarned: number }
  billing: { planBase: number; extraProjectsCost: number; totalMonthly: number }
}

// Plan cards derive from the shared plan catalog (src/shared/plans.ts) — same
// keys/prices/limits as before; change pricing there, not here.
const PLANS = getBillingPlans()
// Plan rank (STARTER < PRO < BUILDER) — used to tell a paid upgrade (which
// must show a confirmation modal before the immediate prorated charge) from a
// downgrade (which shows the safe period-end message).
const PLAN_RANK = getPlanRank()

// Features per plan — what's included
const PLAN_FEATURES: Record<string, { en: string; es: string; starter: boolean; pro: boolean; builder: boolean }[]> = {
  core: [
    { en: 'Dashboard + Project Lifecycle', es: 'Dashboard + Ciclo del Proyecto', starter: true, pro: true, builder: true },
    { en: 'Basic Budget Tracking', es: 'Presupuesto Básico', starter: true, pro: true, builder: true },
    { en: 'Contacts Management', es: 'Gestión de Contactos', starter: true, pro: true, builder: true },
    { en: 'WhatsApp + SMS (click-to-chat)', es: 'WhatsApp + SMS (click-to-chat)', starter: true, pro: true, builder: true },
    { en: 'Calendar Sync (Google/Apple)', es: 'Sincronización de Calendario', starter: true, pro: true, builder: true },
    { en: 'Basic Gantt Chart', es: 'Diagrama Gantt Básico', starter: true, pro: true, builder: true },
    { en: 'Progress Photos (50/project)', es: 'Fotos de Progreso (50/proyecto)', starter: true, pro: true, builder: true },
    { en: 'Documents (10/project)', es: 'Documentos (10/proyecto)', starter: true, pro: true, builder: true },
    { en: 'Limited AI review (fair use)', es: 'Revisión IA limitada (uso justo)', starter: true, pro: true, builder: true },
  ],
  pro: [
    { en: 'AI quote, proposal & receipt review', es: 'Lectura IA de cotizaciones, propuestas y recibos', starter: false, pro: true, builder: true },
    { en: 'Finish Selections Wizard', es: 'Wizard de Selección de Acabados', starter: false, pro: true, builder: true },
    { en: 'Automatic Expense Sheet', es: 'Hoja de Gastos Automática', starter: false, pro: true, builder: true },
    { en: 'Custom Receipt/Doc Categories', es: 'Categorías Custom de Recibos/Docs', starter: false, pro: true, builder: true },
    { en: 'Bid Comparison (scope gaps)', es: 'Comparación de Propuestas (vacíos de alcance)', starter: false, pro: true, builder: true },
    { en: 'Auto Payment Calendar', es: 'Calendario de Pagos Automático', starter: false, pro: true, builder: true },
    { en: 'Missing Payment Terms Alerts', es: 'Alertas de Términos Faltantes', starter: false, pro: true, builder: true },
    { en: 'Material Ordering Schedule', es: 'Programa de Pedidos de Material', starter: false, pro: true, builder: true },
    { en: 'Full Gantt with Dependencies', es: 'Gantt Completo con Dependencias', starter: false, pro: true, builder: true },
    { en: 'AI Email Generator', es: 'Generador de Emails con IA', starter: false, pro: true, builder: true },
    { en: 'Unlimited Photos & Documents', es: 'Fotos y Documentos Ilimitados', starter: false, pro: true, builder: true },
  ],
  builder: [
    { en: 'Advanced AI capacity', es: 'Mayor capacidad de IA', starter: false, pro: false, builder: true },
    { en: 'WhatsApp Business API (direct send)', es: 'WhatsApp Business API (envío directo)', starter: false, pro: false, builder: true },
    { en: 'SMS Direct from App', es: 'SMS Directo desde la App', starter: false, pro: false, builder: true },
    { en: 'Message Templates Library', es: 'Biblioteca de Templates de Mensajes', starter: false, pro: false, builder: true },
    { en: 'Auto Design RFQ Generation', es: 'Generación Automática de RFQ', starter: false, pro: false, builder: true },
  ],
}

// FAQ items
const FAQ_ITEMS = [
  {
    en: 'What happens if my payment fails?',
    es: '¿Qué pasa si mi pago falla?',
    answerEn: 'After the first failed payment, you get a 7-day grace period with full access and a yellow banner reminder. If the second payment also fails, your account is locked and you\'ll see 3 options: (A) Pay your monthly subscription to reactivate everything, (B) Pay $5/month for read-only archive access — you can see all your data but cannot edit or use AI features, (C) Do nothing — you have 90 days to decide before data is deleted.',
    answerEs: 'Después del primer pago fallido, tienes 7 días de gracia con acceso completo y un banner amarillo de recordatorio. Si el segundo pago también falla, tu cuenta se bloquea y verás 3 opciones: (A) Pagar tu mensualidad para reactivar todo, (B) Pagar $5/mes por acceso de solo lectura — puedes ver toda tu información pero no editar ni usar funciones de IA, (C) No hacer nada — tienes 90 días para decidir antes de que se borren los datos.',
  },
  {
    en: 'Can I pause my subscription between projects?',
    es: '¿Puedo pausar mi suscripción entre proyectos?',
    answerEn: 'Yes! We\'re the only construction management platform that offers free pause. For just $5/month, your account goes into read-only mode. All your data, documents, and project history stays safe. Resume anytime to get full access back instantly. No other competitor offers this.',
    answerEs: 'Sí! Somos la única plataforma de gestión de construcción que ofrece pausa. Por solo $5/mes, tu cuenta pasa a modo de solo lectura. Todos tus datos, documentos e historial de proyectos se mantienen seguros. Reanuda en cualquier momento para recuperar el acceso completo. Ningún competidor ofrece esto.',
  },
  {
    en: 'Can I download my data?',
    es: '¿Puedo descargar mis datos?',
    answerEn: 'Yes. You can export your project data, budgets, receipts, contacts, and documents as CSV files at any time — even in read-only mode or archive mode. Your data belongs to you.',
    answerEs: 'Sí. Puedes exportar tus datos de proyectos, presupuestos, recibos, contactos y documentos como archivos CSV en cualquier momento — incluso en modo de solo lectura o modo archivo. Tus datos te pertenecen.',
  },
  {
    en: 'How many users can access my account?',
    es: '¿Cuántos usuarios pueden acceder a mi cuenta?',
    answerEn: 'Starter includes up to 2 users. Pro includes up to 5 users (you + 4 team members). Growth includes up to 10 users. For larger teams, contact us about our Enterprise plan.',
    answerEs: 'Starter incluye hasta 2 usuarios. Pro incluye hasta 5 usuarios (tú + 4 miembros del equipo). Growth incluye hasta 10 usuarios. Para equipos más grandes, contáctanos sobre nuestro plan Enterprise.',
  },
  {
    en: 'Can I change plans at any time?',
    es: '¿Puedo cambiar de plan en cualquier momento?',
    answerEn: 'Yes. Upgrade instantly and the price difference is prorated. Downgrading takes effect at the next billing cycle. No cancellation fees.',
    answerEs: 'Sí. La actualización es instantánea y la diferencia de precio se prorratea. El cambio a un plan menor toma efecto en el siguiente ciclo de facturación. Sin cargos por cancelación.',
  },
  {
    en: 'Is there a free trial?',
    es: '¿Hay prueba gratis?',
    answerEn: 'Yes! Every new account gets 14 days free with all PRO features unlocked. No credit card required. After 14 days, choose the plan that fits your business.',
    answerEs: 'Sí! Cada cuenta nueva obtiene 14 días gratis con todas las funciones PRO desbloqueadas. No se requiere tarjeta de crédito. Después de 14 días, elige el plan que se ajuste a tu negocio.',
  },
]

// ── COMPONENT ─────────────────────────────────────

export default function SubscriptionModule() {
  const { getToken } = useAuth()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isEs = i18n.language === 'es'
  // PR #138A: SubscriptionModule mounts under /projects/:projectId/subscription.
  // Demo projects never have a real Stripe customer/subscription, so any
  // checkout or billing-portal call would fail (or worse, attempt to bill
  // the signed-in user against a context that has nothing to do with the
  // demo). Gate all real billing actions when projectId is the demo id.
  const { projectId } = useParams<{ projectId?: string }>()
  const isDemo = isDemoProject(projectId)

  const goBackToProjects = () => {
    navigate('/projects')
  }
  const [showEnterprise, setShowEnterprise] = useState(false)
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)
  const [showAllFeatures, setShowAllFeatures] = useState(false)
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly')

  // PR-NEW: Stripe checkout-return polling state. When Stripe
  // redirects the user to `/subscription?success=true&session_id=…`,
  // the webhook may still be in flight on the server. We poll
  // both subscription query keys for up to 30 seconds and show
  // a "Processing subscription update…" banner so the user
  // doesn't think the upgrade failed just because their initial
  // fetch hit the pre-webhook DB row.
  const location = useLocation()
  const [checkoutProcessing, setCheckoutProcessing] = useState(false)
  const [checkoutResult, setCheckoutResult] = useState<
    null | 'success' | 'still-processing'
  >(null)
  // Visible (non-alert) error if a Checkout Session can't be created. The
  // Subscribe button stays available and always requests a fresh session, so
  // it doubles as the "start checkout again" retry.
  const [checkoutError, setCheckoutError] = useState<string | null>(null)
  const preCheckoutPlanRef = useRef<string | null>(null)

  // App-controlled plan change (existing subscribers): result banner.
  // 'ok' = upgrade applied; 'downgrade' / 'same' / 'interval' / 'multiple'
  // / 'payment' / 'error' map to specific bilingual messages.
  const [changePlanResult, setChangePlanResult] = useState<
    null | 'ok' | 'downgrade' | 'same' | 'interval' | 'multiple' | 'payment' | 'error'
  >(null)
  // Paid upgrades open a confirmation modal BEFORE calling change-plan, so a
  // click never triggers an immediate prorated charge without explicit consent.
  // Holds the target plan key/label while the modal is open; null = closed.
  const [pendingUpgrade, setPendingUpgrade] = useState<{ key: string; label: string } | null>(null)

  // PR-NEW: Billing diagnostic state. Read-only, owner-scoped
  // — calls `GET /api/stripe/diagnose` with the same Bearer
  // pattern used by every other fetch in this module. The
  // response is non-leaky by construction (PR #95): every env
  // var is surfaced as 'configured' | 'missing', Stripe IDs are
  // surfaced as booleans only, and unmapped priceIds in
  // liveSubscription are truncated to last-6-char fragments. We
  // therefore render the entire response object directly without
  // re-sanitizing on the client.
  type DiagnoseReport = {
    envFlags: {
      STRIPE_SECRET_KEY: string
      stripeSecretMode: 'missing' | 'test' | 'live' | 'unknown'
      STRIPE_WEBHOOK_SECRET: string
      STRIPE_PRICE_STARTER_MONTHLY: string
      STRIPE_PRICE_STARTER_YEARLY: string
      STRIPE_PRICE_PRO_MONTHLY: string
      STRIPE_PRICE_PRO_YEARLY: string
      STRIPE_PRICE_BUILDER_MONTHLY: string
      STRIPE_PRICE_BUILDER_YEARLY: string
      FRONTEND_URL: string
    }
    user: {
      stripeCustomerLinked: boolean
      stripeSubscriptionLinked: boolean
      plan: string
      effectivePlan: string
      subscriptionStatus: string
    }
    liveSubscription: null | {
      source?: 'linked' | 'customer_fallback'
      status: string
      itemCount: number
      unmappedPriceIdCount: number
      mappedPlans: string[]
      unmappedPriceIdsTruncated: string[]
      foundActiveSubscription?: boolean
    }
    // PR-NEW: broader customer-wide summary the repair UI gates on.
    // Surfaces counts + hasRecoverableSubscription +
    // possibleMultipleActiveSubscriptions so we can refuse the
    // blind pick in the multi-active case (BUILDER → PRO double
    // checkout) and show a Stripe-Dashboard-review warning instead.
    customerSubscriptionSummary?: null | {
      customerSubscriptionCount: number
      activeSubscriptionCount: number
      trialingSubscriptionCount: number
      latestSubscriptionStatus: string | null
      latestSubscriptionMappedPlans: string[]
      latestSubscriptionUnmappedPriceIdCount: number
      latestSubscriptionUnmappedPriceIdsTruncated: string[]
      hasRecoverableSubscription: boolean
      possibleMultipleActiveSubscriptions: boolean
    }
  }
  const [diagnose, setDiagnose] = useState<DiagnoseReport | null>(null)
  const [diagnoseStatus, setDiagnoseStatus] = useState<
    'idle' | 'loading' | 'ok' | 'unauthorized' | 'error'
  >('idle')
  const runDiagnose = async () => {
    setDiagnoseStatus('loading')
    setDiagnose(null)
    try {
      const token = await getToken()
      const res = await apiFetch('/api/stripe/diagnose', {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (res.status === 401 || res.status === 403) {
        setDiagnoseStatus('unauthorized')
        return
      }
      if (!res.ok) {
        setDiagnoseStatus('error')
        return
      }
      const data = (await res.json()) as DiagnoseReport
      setDiagnose(data)
      setDiagnoseStatus('ok')
      // PR-NEW: invalidate the subscription caches whenever
      // diagnose returns fresh data. Diagnose and /api/subscription
      // hit the same DB row, so when an out-of-band repair (CLI,
      // direct webhook replay, ops-fixed Stripe state) lands the
      // user's `['subscription']` cache can fall behind — diagnose
      // would show BUILDER while the main "Current Plan" card
      // still shows STARTER. Refetching both keys here is the
      // self-healing path so the UI catches up the moment the
      // user opens the diagnostic panel.
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['subscription-plan'] })
    } catch {
      setDiagnoseStatus('error')
    }
  }

  // PR-NEW: Self-serve recovery for the disconnected state
  // (stripeCustomerLinked=yes, stripeSubscriptionLinked=no). Calls
  // POST /api/stripe/repair-subscription-link, which asks Stripe
  // for the customer's latest active subscription and links it on
  // the user row. The button is only surfaced when the diagnose
  // result indicates an active sub exists that isn't linked yet
  // (see render logic below). On success we re-run the diagnostic
  // and invalidate the subscription query keys so the rest of the
  // UI sees the new plan/status without a hard reload.
  const [repairStatus, setRepairStatus] = useState<
    | 'idle'
    | 'loading'
    | 'ok'
    | 'unauthorized'
    | 'not_found'
    | 'already_linked'
    | 'multiple_active'
    | 'error'
  >('idle')
  const runRepair = async () => {
    setRepairStatus('loading')
    try {
      const token = await getToken()
      const res = await apiFetch('/api/stripe/repair-subscription-link', {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (res.status === 401 || res.status === 403) {
        setRepairStatus('unauthorized')
        return
      }
      if (res.status === 404) {
        setRepairStatus('not_found')
        return
      }
      if (res.status === 409) {
        // The route uses 409 for two distinct refusals:
        //   - already_linked: nothing to do (no-op)
        //   - multiple_active_subscriptions: must clean up in Stripe Dashboard first
        // Inspect the body to render the right copy.
        try {
          const body = (await res.json()) as { error?: string } | null
          if (body?.error === 'multiple_active_subscriptions') {
            setRepairStatus('multiple_active')
          } else {
            setRepairStatus('already_linked')
          }
        } catch {
          setRepairStatus('already_linked')
        }
        return
      }
      if (!res.ok) {
        setRepairStatus('error')
        return
      }
      setRepairStatus('ok')
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['subscription-plan'] })
      // Re-run diagnose so the panel reflects the new linked state
      // (stripeSubscriptionLinked → yes, liveSubscription.source
      // → 'linked').
      runDiagnose()
    } catch {
      setRepairStatus('error')
    }
  }

  // PR-NEW: previously this fetch was `.then(r => r.json()).catch(() => null)`
  // — a foot-gun that silently consumed error JSON bodies as if
  // they were the success shape. When /api/subscription returned
  // a 500 with `{ error: '…' }` the FE cached that error body as
  // `sub`, every `sub.effectivePlan` read became `undefined`, and
  // the UI fell into the "No disponible" branch even though
  // /api/stripe/diagnose on the same DB row was returning a clean
  // BUILDER. The fix is the standard error-checking pattern: read
  // res.ok first, throw on failure so React Query surfaces the
  // error state to the caller, and let the diagnose-based fallback
  // (below) cover the display.
  const {
    data: sub,
    isLoading,
    isError: isSubError,
  } = useQuery<Subscription>({
    queryKey: ['subscription'],
    retry: 1,
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch('/api/subscription', {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error || err?.message || `subscription_fetch_failed_${res.status}`)
      }
      return res.json()
    },
  })

  // Detect a Stripe checkout return and drive the polling
  // refetch + UI banner. Runs once on mount when the query
  // string contains `success=true`. Both `['subscription']` and
  // `['subscription-plan']` are invalidated on every tick so the
  // SubscriptionModule view and every plan gate elsewhere in the
  // app (gated by useSubscriptionPlan) stay in lockstep.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('success') !== 'true') return

    // Capture the plan we saw right before checkout so we know
    // when the upgrade has actually landed. Falls back to the
    // current cached value or "STARTER" if nothing is cached
    // yet.
    const cached = queryClient.getQueryData<Subscription>(['subscription'])
    const captured =
      ((cached as any)?.effectivePlan as string | undefined) ||
      ((cached as any)?.plan as string | undefined) ||
      'STARTER'
    preCheckoutPlanRef.current = captured
    setCheckoutProcessing(true)
    setCheckoutResult(null)

    const POLL_INTERVAL_MS = 2000
    const POLL_TIMEOUT_MS = 30_000
    const startedAt = Date.now()
    let cancelled = false

    const tick = async () => {
      if (cancelled) return
      // Invalidate BOTH keys so the next refetch hits the
      // server. SubscriptionModule uses ['subscription'];
      // useSubscriptionPlan (the app-wide gate) uses
      // ['subscription-plan']. Without invalidating both, the
      // banner clears but every other module stays locked.
      await queryClient.invalidateQueries({ queryKey: ['subscription'] })
      await queryClient.invalidateQueries({ queryKey: ['subscription-plan'] })

      const latest = queryClient.getQueryData<Subscription>(['subscription'])
      const latestPlan =
        ((latest as any)?.effectivePlan as string | undefined) ||
        ((latest as any)?.plan as string | undefined) ||
        null
      if (latestPlan && latestPlan !== preCheckoutPlanRef.current) {
        cancelled = true
        setCheckoutProcessing(false)
        setCheckoutResult('success')
        // Strip the query params so a refresh doesn't restart
        // polling. `replace: true` avoids adding a history entry.
        navigate('/subscription', { replace: true })
        return
      }

      if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
        cancelled = true
        setCheckoutProcessing(false)
        setCheckoutResult('still-processing')
        return
      }

      setTimeout(tick, POLL_INTERVAL_MS)
    }

    // Kick off immediately + clean up if the user navigates
    // away before polling finishes.
    void tick()
    return () => {
      cancelled = true
    }
    // We intentionally only react to a fresh `?success=true` —
    // navigating between sibling tabs that keep the query string
    // unchanged shouldn't restart polling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  // Clean up checkout-return params after handling so they never linger
  // on /subscription. A canceled checkout (`?cancelled=true`) is stripped
  // here; the success path strips its own `?success=true&session_id=…`
  // above. This keeps the URL clean and prevents stale params from being
  // reprocessed on refresh.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('cancelled') === 'true') {
      navigate('/subscription', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  // PR-NEW: when /api/subscription errors out, auto-run diagnose
  // once so the diagnose-sourced fallback (computed below) can
  // populate the Membership UI without forcing the user to hunt
  // for the "Diagnose billing" button. Idempotent via a ref;
  // never retries on diagnose failure so we don't hammer the
  // server when both endpoints are sick.
  const autoDiagnoseFiredRef = useRef(false)
  useEffect(() => {
    if (
      isSubError &&
      !diagnose &&
      diagnoseStatus !== 'loading' &&
      !autoDiagnoseFiredRef.current
    ) {
      autoDiagnoseFiredRef.current = true
      runDiagnose()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubError, diagnose, diagnoseStatus])

  const generateReferral = useMutation({
    mutationFn: async () => { const token = await getToken(); return apiFetch('/api/referrals/generate', { method: 'POST', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => r.json()) },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscription'] }),
  })

  const buyExtra = useMutation({
    mutationFn: async () => { const token = await getToken(); return apiFetch('/api/projects/buy-extra', { method: 'POST', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => r.json()) },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscription'] }),
  })

  const pauseSub = useMutation({
    mutationFn: async () => { const token = await getToken(); return apiFetch('/api/subscription/pause', { method: 'POST', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => r.json()) },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscription'] }),
  })

  const resumeSub = useMutation({
    mutationFn: async () => { const token = await getToken(); return apiFetch('/api/subscription/resume', { method: 'POST', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) } }).then(r => r.json()) },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscription'] }),
  })

  const upgradePlan = useMutation({
    mutationFn: async (plan: string) => { const token = await getToken(); return apiFetch('/api/subscription/upgrade', {
      method: 'POST', headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan }),
    }).then(r => r.json()) },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['subscription'] }),
  })

  const hasStripeCheckout = !!stripePromise

  // Stripe: create checkout session for a plan
  const stripeCheckout = useMutation({
    mutationFn: async ({ planKey, billing }: { planKey: string; billing: 'monthly' | 'yearly' }) => {
      const token = await getToken()
      const res = await apiFetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId: planKey, planName: planKey, billing }),
      })
      const data = await res.json()
      // PR-NEW: defensive 409 handler — when an existing
      // subscriber's click somehow reaches Checkout (stale
      // diagnose data, race after a recent repair, etc.) the
      // server refuses to create a duplicate subscription and
      // returns 409 with a Stripe Billing Portal URL. Redirect
      // there so the user still lands in a working plan-change
      // flow without seeing the duplicate-create attempt.
      if (
        res.status === 409 &&
        data?.error === 'existing_active_subscription' &&
        data?.portalUrl
      ) {
        window.location.href = data.portalUrl
        return data
      }
      // No silent failure: a failed creation (or a response with no URL)
      // must surface visibly, never leave the user on a dead page.
      // Codex PR2 [P2]: el servidor responde 409 SIN portalUrl. En vez de un
      // error genérico, se abre el portal de Stripe para administrar la
      // suscripción que ya existe.
      if (res.status === 409 && data?.error === 'existing_active_subscription') {
        const pr = await apiFetch('/api/stripe/portal', {
          method: 'POST',
          headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        })
        const pd = await pr.json().catch(() => null)
        if (pr.ok && pd?.url) {
          window.location.href = pd.url
          return data
        }
        throw new Error('existing_active_subscription')
      }
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || `checkout_failed_${res.status}`)
      }
      // Always a FRESH session URL straight from the server (never cached).
      window.location.href = data.url
      return data
    },
    onMutate: () => { setCheckoutError(null) },
    onError: (err: Error) => {
      setCheckoutError(err?.message === 'existing_active_subscription'
        ? (isEs
            ? 'Ya tienes una suscripción activa. Usa "Administrar facturación" para cambiarla.'
            : 'You already have an active subscription. Use "Manage billing" to change it.')
        : (isEs
            ? 'No pudimos iniciar el pago. Inténtalo de nuevo.'
            : 'We could not start checkout. Please try again.'))
    },
  })

  // Stripe: open customer portal for billing management
  // (payment methods / invoices / billing info / cancellation ONLY —
  // plan switching is disabled in the portal config; plan changes go
  // through the app-controlled changePlan mutation below).
  const stripePortal = useMutation({
    mutationFn: async () => {
      const token = await getToken()
      const res = await apiFetch('/api/stripe/portal', {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
      })
      const data = await res.json()
      if (data.url) window.location.href = data.url
      return data
    },
  })

  // App-controlled plan change for existing subscribers (replaces the
  // Customer-Portal plan-switch path). Upgrades apply immediately with
  // proration on the EXISTING subscription (no duplicate sub). Downgrades
  // return an explicit "scheduled at period end / coming soon" message.
  const changePlan = useMutation({
    mutationFn: async ({ plan, billing }: { plan: string; billing: 'monthly' | 'yearly' }) => {
      const token = await getToken()
      const res = await apiFetch('/api/subscription/change-plan', {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, billing }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw { status: res.status, code: data?.error as string | undefined }
      return data
    },
    onSuccess: () => {
      setChangePlanResult('ok')
      setPendingUpgrade(null) // close the confirmation modal
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['subscription-plan'] })
    },
    onError: (err: any) => {
      const code = err?.code as string | undefined
      setChangePlanResult(
        code === 'downgrade_scheduling_not_available' ? 'downgrade'
        : code === 'same_plan' ? 'same'
        : code === 'interval_change_not_supported' ? 'interval'
        : code === 'multiple_active_subscriptions' ? 'multiple'
        : code === 'payment_failed' ? 'payment'
        : 'error',
      )
      // Close the modal so the (prominent, colored) result banner is the
      // single, non-silent surface for the error/outcome.
      setPendingUpgrade(null)
    },
  })

  // Proration PREVIEW for the open upgrade modal. Read-only (Stripe invoice
  // preview — persists nothing). Fires when the modal opens; the Confirm
  // button stays disabled until this succeeds, so a user never confirms a
  // charge without first seeing the estimated amount. retry:false +
  // staleTime:0 → a fresh estimate each time, explicit Retry on failure.
  const previewQuery = useQuery({
    queryKey: ['plan-change-preview', pendingUpgrade?.key, billingPeriod],
    enabled: !!pendingUpgrade,
    retry: false,
    staleTime: 0,
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch('/api/subscription/preview-plan-change', {
        method: 'POST',
        headers: { ...(token ? { 'Authorization': `Bearer ${token}` } : {}), 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: pendingUpgrade!.key, billing: billingPeriod }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error((data?.error as string) || 'preview_failed')
      return data as {
        estimatedAmountDueToday: number
        currency: string | null
        targetPrice: number | null
        nextRenewalDate: string | null
      }
    },
  })

  // Loading state — prevents accessing undefined properties before API responds
  if (isLoading) {
    return (
      <div className="p-5 max-w-5xl mx-auto flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-[#06539B] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">{isEs ? 'Cargando suscripción...' : 'Loading subscription...'}</p>
        </div>
      </div>
    )
  }

  // Safe defaults — every nested object guaranteed even if API returns null/partial.
  // Derived from the STARTER catalog entry so it never carries stale prices/caps.
  const starterCat = PLANS.find((p) => p.key === 'STARTER')
  const sP = starterCat?.price ?? 199
  const sProj = starterCat?.projects ?? 3
  const sScans = starterCat?.scans ?? 20
  const D: Subscription = {
    plan: 'STARTER', paused: false,
    limits: { maxProjects: sProj, aiCallsPerMonth: sScans, priceMonthly: sP, storageGB: starterCat?.storage ?? 3, extraProjectPrice: starterCat?.extra ?? 12 },
    projects: { current: 0, includedInPlan: sProj, extraPurchased: 0, max: sProj, extraProjectPrice: starterCat?.extra ?? 12 },
    aiUsage: { used: 0, max: sScans },
    trial: { active: false, endsAt: '' },
    referrals: { count: 0, freeMonthsEarned: 0 },
    billing: { planBase: sP, extraProjectsCost: 0, totalMonthly: sP },
  }
  // Safely merge — guard against null/undefined sub-objects before spreading
  const s = (v: any, d: any) => (v && typeof v === 'object' ? { ...d, ...v } : d)
  const plan: Subscription = sub
    ? {
        ...D,
        ...sub,
        limits: s(sub.limits, D.limits),
        projects: s(sub.projects, D.projects),
        aiUsage: s(sub.aiUsage, D.aiUsage),
        trial: s(sub.trial, D.trial),
        referrals: s(sub.referrals, D.referrals),
        billing: s(sub.billing, D.billing),
      }
    : D

  // PR-NEW: display-only fallback when /api/subscription is
  // unreachable. /api/subscription and /api/stripe/diagnose both
  // compute effectivePlan via the same deriveEffectivePlan helper
  // against the same DB row, so when /api/subscription throws
  // (downstream Prisma query failure, transient 5xx, etc.) but
  // the diagnose endpoint returns a clean payload, we surface the
  // diagnose-sourced plan on the Membership UI rather than
  // showing "No disponible". This is display-only — gated
  // features still route through useSubscriptionPlan (which has
  // its own fail-closed STARTER default).
  const subFromDiagnose: Partial<Subscription> | null =
    isSubError && diagnose?.user
      ? {
          plan: diagnose.user.plan,
          currentPlan: diagnose.user.plan,
          effectivePlan: diagnose.user.effectivePlan,
          subscriptionStatus: diagnose.user.subscriptionStatus,
          stripeCustomerLinked: diagnose.user.stripeCustomerLinked,
          stripeSubscriptionLinked: diagnose.user.stripeSubscriptionLinked,
        }
      : null

  // PR-NEW: server-authoritative current plan. Prefer
  // `effectivePlan` (deriveEffectivePlan output — already
  // downgrades CANCELLED/INCOMPLETE to STARTER), then
  // `currentPlan` (PR #95 alias), then the legacy `plan`
  // field. Read first from the canonical `sub` (when /api/subscription
  // succeeded), then from `subFromDiagnose` (when /api/subscription
  // failed but the diagnose endpoint succeeded). Falling all the
  // way through to `''` surfaces the "Unavailable" notice rather
  // than masquerading as STARTER.
  const planSource = sub ?? subFromDiagnose
  const currentPlanKey: string =
    (planSource?.effectivePlan || planSource?.currentPlan || planSource?.plan || '') as string
  const hasResolvedPlan = !!currentPlanKey
  const isUsingDiagnoseFallback = !sub && !!subFromDiagnose

  // PR-NEW: server-authoritative plan-stats hydration. When the
  // diagnose fallback supplies the current plan (because
  // /api/subscription failed), `plan.limits` / `plan.projects`
  // / `plan.billing` still come from the structural `D` default
  // — which is hardcoded STARTER. That produced the
  // "BUILDER + $39/mo" mixed-display bug. The fix is to derive
  // a single `displayPlanData` from PLANS keyed on
  // `currentPlanKey`, then prefer real API values when /api/subscription
  // succeeded, else the PLANS lookup. Same idea for derived
  // limits/projects/users/storage.
  const displayPlanData = PLANS.find((p) => p.key === currentPlanKey) || null
  // Prefer the user's REAL current Stripe price (PR-1 /api/subscription field);
  // fall back to the catalog price when there is no linked subscription. This
  // is what prevents a display/charge mismatch for grandfathered/test users.
  const displayPriceMonthly: number =
    sub?.currentPriceMonthly ?? sub?.limits?.priceMonthly ?? displayPlanData?.price ?? 0
  const displayMaxProjects: number =
    sub?.projects?.max ?? sub?.limits?.maxProjects ?? displayPlanData?.projects ?? 0
  const displayUsers: number = displayPlanData?.users || 1
  const displayStorageGB: number =
    sub?.limits?.storageGB ?? displayPlanData?.storage ?? 0
  const displayExtraPrice: number =
    sub?.limits?.extraProjectPrice ?? displayPlanData?.extra ?? 12

  // PR-NEW: route plan-change clicks through Stripe Billing
  // Portal when the user already has a linked subscription
  // (display sourced from either /api/subscription or the
  // diagnose fallback). For first-time payers (no linked sub)
  // we still use Stripe Checkout. This pin parallels what PR #99
  // added for the canonical sub path; here it also fires for
  // the diagnose-fallback path so a linked BUILDER no longer
  // sees "Subscribe" on Pro/Starter.
  const isExistingSubscriber: boolean =
    (planSource as any)?.stripeSubscriptionLinked === true

  return (
    <div className="p-5 pb-10 max-w-5xl mx-auto flex flex-col gap-5">
      <button
        type="button"
        onClick={goBackToProjects}
        className="flex items-center gap-1.5 text-[11px] font-[700] text-[#9E9890] hover:text-[#06539B] transition-colors self-start -mb-2"
      >
        <ChevronLeft size={14} />
        {isEs ? 'Volver a Proyectos' : 'Back to Projects'}
      </button>
      <div>
        <h2 className="text-[18px] font-[900] tracking-[-0.5px] text-[#1C1A17]">{isEs ? 'Membresía' : 'Membership'}</h2>
        <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">{isEs ? 'Administra tu plan, facturación y upgrades.' : 'Manage your plan, billing, and upgrades.'}</p>
      </div>

      {/* PR-NEW: explicit "subscription unavailable" notice when
          /api/subscription returned null/error AND we're past the
          loading state. Replaces the previous silent fallback to
          STARTER, which masked real backend errors behind a
          plausible-looking plan card. */}
      {!isLoading && !hasResolvedPlan && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[12px] font-[700] text-amber-800">
          {isEs
            ? 'No se pudo cargar la suscripción. Recarga la página o ejecuta el diagnóstico.'
            : 'Could not load subscription. Refresh the page or run the diagnostic.'}
        </div>
      )}

      {/* PR-NEW: non-blocking notice when /api/subscription failed
          but the diagnose endpoint succeeded. The plan/UI below
          is sourced from diagnose, so the user sees the right
          plan without being locked into a "No disponible" state.
          Gated features still use useSubscriptionPlan (server-
          authoritative); this is display-only. */}
      {!isLoading && isUsingDiagnoseFallback && (
        <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-[12px] font-[700] text-[#B45309]">
          {isEs
            ? 'Plan mostrado desde el diagnóstico de Stripe (la API de suscripción no respondió). Recarga la página para volver a intentarlo.'
            : 'Plan shown from Stripe diagnostic (subscription API did not respond). Refresh the page to retry.'}
        </div>
      )}

      {/* PR-NEW: checkout-return banner. Driven by the
          useEffect above; mutually-exclusive states. The
          banners sit above the Trial / Paused banners so the
          user sees the upgrade outcome before the legacy
          status messaging. */}
      {checkoutProcessing && (
        <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-4 flex items-center gap-3">
          <div className="h-5 w-5 border-2 border-[#06539B] border-t-transparent rounded-full animate-spin shrink-0" />
          <div>
            <div className="text-[12px] font-[800] text-[#06539B]">
              {isEs ? 'Procesando actualización…' : 'Processing subscription update…'}
            </div>
            <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
              {isEs
                ? 'Stripe confirmó el pago. Estamos esperando la confirmación del servidor.'
                : 'Stripe confirmed the payment. Waiting for the server to confirm the upgrade.'}
            </p>
          </div>
        </div>
      )}
      {checkoutResult === 'success' && (
        <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-xl p-4 flex items-center gap-3">
          <Check size={20} className="text-[#047857] shrink-0" />
          <div className="text-[12px] font-[800] text-[#047857]">
            {isEs ? '¡Suscripción actualizada!' : 'Subscription updated!'}
          </div>
        </div>
      )}
      {checkoutError && (
        <div data-testid="checkout-error" className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between gap-3">
          <div className="text-[12px] font-[800] text-red-800">{checkoutError}</div>
          <button
            onClick={() => setCheckoutError(null)}
            className="text-[11px] font-[800] text-red-700 underline shrink-0"
          >
            {isEs ? 'Cerrar' : 'Dismiss'}
          </button>
        </div>
      )}
      {checkoutResult === 'still-processing' && (
        <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-xl p-4 flex items-start gap-3">
          <div className="shrink-0 mt-0.5">
            <CreditCard size={18} className="text-[#B45309]" />
          </div>
          <div className="flex-1">
            <div className="text-[12px] font-[800] text-[#B45309]">
              {isEs
                ? 'Pago completado. La suscripción todavía se está procesando.'
                : 'Payment completed. Subscription is still processing.'}
            </div>
            <p className="text-[11px] font-[600] text-[#9E9890] mt-0.5">
              {isEs
                ? 'Recarga la página en un momento o contáctanos si el plan no se actualiza pronto.'
                : 'Refresh the page in a moment, or contact us if the plan does not update soon.'}
            </p>
            {/* PR-NEW: inline diagnostic call-to-action so the
                user can self-serve the env / webhook check after
                a polling timeout. */}
            <button
              type="button"
              onClick={runDiagnose}
              disabled={diagnoseStatus === 'loading'}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-[800] text-[#B45309] hover:text-[#7C2D12] underline disabled:opacity-50"
            >
              {diagnoseStatus === 'loading'
                ? (isEs ? 'Diagnosticando…' : 'Diagnosing…')
                : (isEs ? 'Ejecutar diagnóstico de pago' : 'Run billing diagnosis')}
            </button>
          </div>
        </div>
      )}

      {/* Trial banner */}
      {plan.trial?.active && (
        <div className="bg-gradient-to-r from-[#06539B] to-[#EC4899] rounded-xl p-4 text-white">
          <div className="flex items-center gap-2 mb-1">
            <Star size={16} />
            <span className="text-[13px] font-[800]">{isEs ? 'Prueba Gratis' : 'Free Trial'}</span>
          </div>
          <p className="text-[11px] font-[600] opacity-90">
            {isEs ? 'Todas las funciones PRO incluidas · 14 días · Sin tarjeta de crédito' : 'All PRO features included · 14 days · No credit card required'}
          </p>
        </div>
      )}

      {/* Paused banner */}
      {plan.paused && (
        <div className="bg-gradient-to-r from-[#6B7280] to-[#374151] rounded-xl p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Pause size={16} />
                <span className="text-[13px] font-[800]">{isEs ? 'Suscripción Pausada' : 'Subscription Paused'}</span>
              </div>
              <p className="text-[11px] font-[600] opacity-90">
                {isEs ? '$5/mes — Tu información está segura. Reanuda cuando quieras.' : '$5/mo — Your data is safe. Resume anytime.'}
              </p>
            </div>
            <button onClick={() => resumeSub.mutate()} disabled={resumeSub.isPending}
              className="flex items-center gap-1.5 bg-white text-[#1C1A17] text-[13px] font-[800] px-4 py-2 rounded-full hover:bg-gray-100">
              <Play size={12} /> {resumeSub.isPending ? '...' : (isEs ? 'Reanudar' : 'Resume')}
            </button>
          </div>
        </div>
      )}

      {/* Current plan stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 border-l-[3px] border-l-[#06539B]">
          <div className="flex items-center gap-2 mb-2">
            <Crown size={16} className="text-[#06539B]" />
            {/* Codex PR2 [P2]: sin suscripción ligada no hay "plan actual" que se
                esté pagando; se dice qué nivel de acceso tiene la cuenta. */}
            <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]" data-testid="subscription-summary-label">
              {planSource?.stripeSubscriptionLinked === true || isDemo
                ? (isEs ? 'Plan Actual' : 'Current Plan')
                : sub?.trial?.active
                  ? (isEs ? 'Prueba gratis' : 'Free trial')
                  : (isEs ? 'Nivel de acceso' : 'Access level')}
            </span>
          </div>
          <div className="text-[22px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
            {currentPlanKey || (isEs ? 'No disponible' : 'Unavailable')}
          </div>
          <div className="text-[14px] font-[800] text-[#06539B] mt-1">
            {plan.paused
              ? <span className="text-[#9E9890]">{isEs ? 'Pausado' : 'Paused'} · $5/mo</span>
              : planSource?.stripeSubscriptionLinked === true
                ? <>${displayPriceMonthly}/mo</>
                // PR2: no linked subscription → nothing is being charged. Never
                // show the list price as if it were the current bill.
                : <span data-testid="subscription-current-plan-no-billing" className="text-[#9E9890]">
                    {sub?.trial?.active
                      ? (isEs ? 'Prueba gratis — sin cobro' : 'Free trial — not billed')
                      : (isEs ? 'Sin suscripción activa' : 'No active subscription')}
                  </span>}
          </div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Zap size={16} className="text-[#00BCD4]" />
            <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">AI Scans</span>
          </div>
          <div className="text-[22px] font-[900] tracking-[-0.5px] text-[#1C1A17]">
            {sub?.aiUsage?.used ?? (isUsingDiagnoseFallback ? '—' : 0)}
          </div>
          <div className="text-[9px] font-[700] text-[#22C55E] mt-1">{currentPlanKey === 'STARTER' ? (isEs ? 'Limitado — uso justo' : 'Limited — fair use') : (isEs ? 'Incluido — uso justo' : 'Included — fair use')}</div>
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <FolderOpen size={16} className="text-[#F59E0B]" />
            <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">{isEs ? 'Proyectos' : 'Projects'}</span>
          </div>
          <div className="text-[22px] font-[900] text-[#1C1A17]">
            {sub?.projects?.current ?? (isUsingDiagnoseFallback ? '—' : 0)}/{displayMaxProjects}
          </div>
          {!plan.paused && !isUsingDiagnoseFallback && (
            <button onClick={() => buyExtra.mutate()} className="flex items-center gap-1 mt-1 text-[10px] font-[800] text-[#3B82F6]">
              <Plus size={12} /> +${displayExtraPrice}/{isEs ? 'extra' : 'extra'}
            </button>
          )}
        </div>
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} className="text-[#14B8A6]" />
            <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">{isEs ? 'Usuarios' : 'Users'}</span>
          </div>
          <div className="text-[22px] font-[900] text-[#1C1A17]">{displayUsers}</div>
          <div className="text-[9px] font-[700] text-[#9E9890] mt-1">+$8/{isEs ? 'usuario extra' : 'extra user'}</div>
        </div>
      </div>

      {/* ── PLAN COMPARISON ── */}
      <div className="bg-white border border-[#ECEAE4] rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[#ECEAE4] flex items-center justify-between">
          <div>
            <h3 className="text-[14px] font-[900]">{isEs ? 'Comparar Planes' : 'Compare Plans'}</h3>
            <p className="text-[10px] font-[600] text-[#9E9890] mt-0.5">
              {isEs ? 'Todos los planes incluyen pausa gratis — $5/mes entre proyectos' : 'All plans include free pause — $5/mo between projects'}
            </p>
          </div>
          <div className="flex items-center gap-1 bg-[#F2F0EB] rounded-full p-0.5">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`text-[10px] font-[800] px-3 py-1.5 rounded-full transition-colors ${billingPeriod === 'monthly' ? 'bg-white text-[#1C1A17] shadow-sm' : 'text-[#9E9890]'}`}
            >
              {isEs ? 'Mensual' : 'Monthly'}
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`text-[10px] font-[800] px-3 py-1.5 rounded-full transition-colors ${billingPeriod === 'yearly' ? 'bg-white text-[#1C1A17] shadow-sm' : 'text-[#9E9890]'}`}
            >
              {isEs ? 'Anual' : 'Yearly'} <span className="text-[#22C55E]">-20%</span>
            </button>
          </div>
        </div>

        {/* F2 — Responsive UX Parity Audit:
            The plan comparison is a 4-col matrix (1 label/spacer + 3
            plan columns). On mobile (<sm) 4 cols squeeze to ~80px
            each at 320px viewport and the $price + plan label
            overlap. We preserve the desktop matrix EXACTLY (no
            content change, no business-logic change) and let mobile
            scroll horizontally instead. Min width is sized so each
            plan column shows the price + Subscribe button without
            truncation. */}
        <div className="overflow-x-auto">
          <div className="min-w-[520px]">

        {/* Plan headers */}
        <div className="grid grid-cols-4 border-b border-[#ECEAE4]">
          <div className="p-4" />
          {PLANS.map(p => {
            // PR2: a plan is only "current" when it is actually being paid
            // (linked Stripe subscription). A cancelled / trial account must
            // be able to subscribe to ANY plan, Starter included.
            const isCurrent = p.key === currentPlanKey && (isDemo || planSource?.stripeSubscriptionLinked === true)
            return (
              <div key={p.key} className={`p-4 text-center relative ${isCurrent ? 'bg-[#06539B]/5' : ''} ${p.key !== 'BUILDER' ? 'border-r border-[#ECEAE4]' : ''}`}>
                {p.popular && (
                  <div className="absolute -top-0 left-1/2 -translate-x-1/2 bg-[#06539B] text-white text-[7px] font-[800] uppercase tracking-[0.5px] px-2.5 py-0.5 rounded-b-md">
                    {isEs ? 'Más Popular' : 'Most Popular'}
                  </div>
                )}
                <div className="text-[14px] font-[900] text-[#1C1A17]">{p.label}</div>
                <div className="text-[24px] font-[900] text-[#06539B] mt-1">
                  ${billingPeriod === 'yearly' ? Math.round((p.priceYearly ?? p.price * 12 * 0.8) / 12) : p.price}
                  <span className="text-[10px] font-[600] text-[#9E9890]">/{billingPeriod === 'yearly' ? (isEs ? 'mes' : 'mo') : 'mo'}</span>
                </div>
                {billingPeriod === 'yearly' && p.priceYearly != null && (
                  <>
                    <div className="text-[9px] font-[700] text-[#22C55E] mt-0.5">
                      {isEs ? `$${p.priceYearly}/año — ahorras $${p.price * 12 - p.priceYearly}` : `$${p.priceYearly}/yr — save $${p.price * 12 - p.priceYearly}`}
                    </div>
                    <div className="text-[8px] font-[600] text-[#9E9890] mt-0.5">
                      {isEs ? 'Cobrado anualmente' : 'Billed annually'}
                    </div>
                  </>
                )}
                <div
                  data-testid="plan-card-tax-notice"
                  className="text-[8px] font-[600] text-[#9E9890] mt-0.5 leading-tight"
                >
                  {isEs
                    ? 'Los precios no incluyen impuestos. Se agregarán los impuestos sobre ventas que apliquen según tu ubicación.'
                    : 'Prices exclude applicable taxes. Sales tax will be added where required based on your location.'}
                </div>
                <div className="flex items-center justify-center gap-1 mt-1.5">
                  <Users size={10} className="text-[#9E9890]" />
                  <span className="text-[12px] font-[700] text-[#9E9890]">
                    {p.users} {isEs ? (p.users === 1 ? 'usuario' : 'usuarios') : (p.users === 1 ? 'user' : 'users')}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-1 mt-0.5">
                  <HardDrive size={10} className="text-[#9E9890]" />
                  <span className="text-[12px] font-[700] text-[#9E9890]">{p.storage} GB</span>
                </div>
                {isCurrent ? (
                  <div className="mt-3 text-[10px] font-[800] py-2 rounded-full bg-[#06539B] text-white">{isEs ? 'Plan Actual' : 'Current Plan'}</div>
                ) : isDemo ? (
                  // PR #138A: demo can never initiate a real Stripe
                  // checkout/portal. Render a static, non-interactive
                  // chip in place of the change-plan button.
                  <div
                    className="mt-3 text-[10px] font-[800] py-2 rounded-full border border-[#CECAC4] text-[#9E9890] bg-[#F7F5F2]"
                    data-testid="subscription-plan-demo-locked"
                  >
                    {isEs ? 'Solo en proyectos guardados' : 'Saved projects only'}
                  </div>
                ) : (
                  // Existing subscribers change plans through the
                  // app-controlled changePlan endpoint (immediate
                  // prorated upgrade on the existing subscription;
                  // downgrades return a period-end message). New paid
                  // users still use Checkout. The Customer Portal is no
                  // longer used for plan switching (it exposed legacy
                  // products/prices) — only for billing/cancel below.
                  <button
                    onClick={() => {
                      if (!hasStripeCheckout) return
                      if (isExistingSubscriber) {
                        setChangePlanResult(null)
                        // Paid UPGRADE → confirm first (immediate prorated
                        // charge). Downgrade/same → existing flow (the backend
                        // returns the safe period-end / same-plan message; no
                        // access is reduced). Rank is undefined for an
                        // unresolved current plan, so we fall through to the
                        // existing flow rather than guessing.
                        const isUpgrade =
                          currentPlanKey in PLAN_RANK &&
                          p.key in PLAN_RANK &&
                          (PLAN_RANK as Record<string, number>)[p.key] >
                            (PLAN_RANK as Record<string, number>)[currentPlanKey]
                        if (isUpgrade) {
                          setPendingUpgrade({ key: p.key, label: p.label })
                        } else {
                          changePlan.mutate({ plan: p.key, billing: billingPeriod })
                        }
                      } else {
                        stripeCheckout.mutate({ planKey: p.key, billing: billingPeriod })
                      }
                    }}
                    disabled={
                      stripeCheckout.isPending ||
                      changePlan.isPending ||
                      upgradePlan.isPending
                    }
                    className={`mt-3 w-full text-[10px] font-[800] py-2 rounded-full border transition-colors ${hasStripeCheckout ? 'border-[#06539B] text-[#06539B] hover:bg-[#06539B] hover:text-white' : 'border-[#CECAC4] text-[#9E9890] cursor-not-allowed bg-[#F7F5F2]'}`}>
                    {stripeCheckout.isPending || changePlan.isPending
                      ? '...'
                      : isExistingSubscriber
                        ? (isEs ? `Cambiar a ${p.label}` : `Change to ${p.label}`)
                        : (isEs ? 'Suscribirse' : 'Subscribe')}
                  </button>
                )}
                {!isCurrent && !hasStripeCheckout && (
                  <div className="mt-2 text-[9px] font-[700] text-amber-600">
                    {isEs ? 'Stripe no configurado en este entorno.' : 'Stripe is not configured in this environment.'}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Paid-upgrade confirmation modal — opens before any change-plan
            call so a click never triggers an immediate prorated charge
            without explicit confirmation. It loads a read-only proration
            PREVIEW (estimated charge today) and only enables "Confirm
            upgrade" once that estimate is available. The change-plan API
            call happens only on "Confirm upgrade". */}
        {pendingUpgrade && (() => {
          const targetLabel = pendingUpgrade.label
          const currentLabel = PLANS.find((pp) => pp.key === currentPlanKey)?.label ?? currentPlanKey
          const pv = previewQuery.data
          const intervalSuffix = billingPeriod === 'yearly' ? (isEs ? 'año' : 'yr') : (isEs ? 'mes' : 'mo')
          const targetPrice = pv?.targetPrice ?? PLANS.find((pp) => pp.key === pendingUpgrade.key)?.price ?? null
          const estimate = typeof pv?.estimatedAmountDueToday === 'number' ? pv.estimatedAmountDueToday : null
          const renewal = pv?.nextRenewalDate
            ? new Date(pv.nextRenewalDate).toLocaleDateString(isEs ? 'es' : 'en', { year: 'numeric', month: 'short', day: 'numeric' })
            : null
          return (
            <div
              data-testid="subscription-upgrade-confirm-modal"
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
              onClick={() => { if (!changePlan.isPending) setPendingUpgrade(null) }}
            >
              <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
                <h3 className="text-[15px] font-[800] text-[#1C1A17]">
                  {isEs ? `¿Cambiar a ${targetLabel}?` : `Upgrade to ${targetLabel}?`}
                </h3>
                <div className="mt-2 space-y-2 text-[12px] font-[600] leading-relaxed text-[#6B6660]">
                  <p>
                    {isEs
                      ? `Estás cambiando de ${currentLabel} a ${targetLabel}.`
                      : `You are upgrading from ${currentLabel} to ${targetLabel}.`}
                  </p>
                  <p>
                    {isEs
                      ? `Este upgrade se aplicará inmediatamente. Stripe aplicará un crédito por la parte no usada de tu plan ${currentLabel} y cobrará la diferencia prorrateada por el periodo restante.`
                      : `Your upgrade will apply immediately. Stripe will apply a credit for the unused portion of your ${currentLabel} plan and charge the prorated difference for the remaining billing period.`}
                  </p>

                  {/* Estimate block: loading / error+retry / estimate */}
                  <div data-testid="subscription-upgrade-estimate" className="rounded-lg bg-[#F7F5F2] p-3">
                    {previewQuery.isLoading || previewQuery.isFetching ? (
                      <div className="flex items-center gap-2 text-[#9E9890]">
                        <div className="h-3 w-3 rounded-full border-2 border-[#06539B] border-t-transparent animate-spin" />
                        {isEs ? 'Calculando cargo estimado…' : 'Calculating estimated charge…'}
                      </div>
                    ) : previewQuery.isError ? (
                      <div data-testid="subscription-upgrade-preview-error" className="text-red-700">
                        <p className="font-[700]">
                          {isEs ? 'No pudimos calcular el cargo estimado.' : 'We could not calculate the estimated charge.'}
                        </p>
                        <button
                          data-testid="subscription-upgrade-preview-retry"
                          onClick={() => previewQuery.refetch()}
                          className="mt-1 font-[800] underline"
                        >
                          {isEs ? 'Reintentar' : 'Retry'}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <div data-testid="subscription-upgrade-estimate-amount" className="font-[800] text-[#1C1A17]">
                          {isEs ? 'Cargo estimado hoy: ' : 'Estimated charge today: '}
                          {estimate != null ? `$${estimate.toFixed(2)}` : '—'}
                        </div>
                        <div className="font-[600] text-[#6B6660]">
                          {isEs ? 'Nuevo plan: ' : 'New plan: '}
                          {targetLabel}{targetPrice != null ? ` — $${targetPrice}/${intervalSuffix}` : ''}
                        </div>
                        {renewal && (
                          <div className="font-[600] text-[#6B6660]">
                            {isEs ? 'Próxima renovación: ' : 'Next renewal: '}{renewal}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <p className="text-[10px] text-[#9E9890]">
                    {isEs
                      ? 'Los precios no incluyen impuestos. Se agregarán los impuestos sobre ventas que apliquen según tu ubicación.'
                      : 'Prices exclude applicable taxes. Sales tax will be added where required based on your location.'}
                  </p>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                  <button
                    data-testid="subscription-upgrade-cancel"
                    onClick={() => setPendingUpgrade(null)}
                    disabled={changePlan.isPending}
                    className="rounded-full border border-[#CECAC4] px-4 py-2 text-[11px] font-[800] text-[#6B6660] hover:bg-[#F7F5F2] disabled:opacity-50"
                  >
                    {isEs ? 'Cancelar' : 'Cancel'}
                  </button>
                  <button
                    data-testid="subscription-upgrade-confirm"
                    onClick={() => changePlan.mutate({ plan: pendingUpgrade.key, billing: billingPeriod })}
                    disabled={changePlan.isPending || !previewQuery.isSuccess}
                    className="rounded-full bg-[#06539B] px-4 py-2 text-[11px] font-[800] text-white hover:bg-[#044075] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {changePlan.isPending
                      ? (isEs ? 'Procesando…' : 'Processing…')
                      : (isEs ? 'Confirmar upgrade' : 'Confirm upgrade')}
                  </button>
                </div>
              </div>
            </div>
          )
        })()}

        {/* App-controlled plan-change result banner */}
        {changePlanResult && (
          <div
            data-testid="subscription-change-plan-result"
            className={`mx-4 mb-3 rounded-lg px-3 py-2 text-[11px] font-[700] border ${
              changePlanResult === 'ok'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : changePlanResult === 'downgrade' || changePlanResult === 'same' || changePlanResult === 'interval'
                ? 'bg-amber-50 border-amber-200 text-amber-800'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {changePlanResult === 'ok' && (isEs ? '¡Plan actualizado! Tu nuevo plan ya está activo y se prorrateó la diferencia.' : 'Plan updated! Your new plan is active and the difference was prorated.')}
            {changePlanResult === 'downgrade' && (isEs ? 'Los cambios a un plan menor toman efecto al final de tu período de facturación (próximamente). Mantienes el acceso completo hasta entonces. Para cancelar, usa Gestionar Pagos.' : 'Downgrades take effect at the end of your billing period (coming soon). You keep full access until then. To cancel instead, use Manage Billing.')}
            {changePlanResult === 'same' && (isEs ? 'Ya estás en este plan.' : 'You are already on this plan.')}
            {changePlanResult === 'interval' && (isEs ? 'El cambio entre facturación mensual y anual no está disponible aquí todavía. Cambia de plan en el mismo intervalo.' : 'Switching between monthly and annual billing is not available here yet. Change plan on the same interval.')}
            {changePlanResult === 'multiple' && (isEs ? 'Se encontraron varias suscripciones activas. Revisa y cancela duplicados en Stripe antes de cambiar de plan.' : 'Multiple active subscriptions found. Review and cancel duplicates in Stripe before changing plans.')}
            {changePlanResult === 'payment' && (isEs ? 'No se pudo cobrar la diferencia prorrateada. Tu plan no cambió. Revisa tu método de pago e inténtalo de nuevo.' : 'We could not collect the prorated charge. Your plan was not changed. Check your payment method and try again.')}
            {changePlanResult === 'error' && (isEs ? 'No se pudo cambiar el plan. Inténtalo de nuevo.' : 'Could not change the plan. Please try again.')}
          </div>
        )}

        {/* Feature rows */}
        {/* Core features */}
        <div className="px-4 py-2 bg-[#FAFAF8] border-b border-[#ECEAE4]">
          <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890]">
            {isEs ? 'Funciones Incluidas en Todos' : 'Included in All Plans'}
          </span>
        </div>
        {PLAN_FEATURES.core.map((f, i) => (
          <FeatureRow key={`core-${i}`} label={isEs ? f.es : f.en} starter={f.starter} pro={f.pro} builder={f.builder} />
        ))}

        {/* PRO features */}
        <div className="px-4 py-2 bg-[#06539B]/5 border-b border-[#ECEAE4]">
          <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#06539B] flex items-center gap-1">
            <Crown size={10} /> PRO
          </span>
        </div>
        {PLAN_FEATURES.pro.slice(0, showAllFeatures ? undefined : 5).map((f, i) => (
          <FeatureRow key={`pro-${i}`} label={isEs ? f.es : f.en} starter={f.starter} pro={f.pro} builder={f.builder} />
        ))}
        {!showAllFeatures && PLAN_FEATURES.pro.length > 5 && (
          <button onClick={() => setShowAllFeatures(true)}
            className="w-full py-2 text-[10px] font-[800] text-[#06539B] hover:bg-[#06539B]/5 border-b border-[#ECEAE4]">
            +{PLAN_FEATURES.pro.length - 5} {isEs ? 'más' : 'more'}... <ChevronDown size={10} className="inline" />
          </button>
        )}

        {/* GROWTH features (internal key remains BUILDER; public label is GROWTH) */}
        <div className="px-4 py-2 bg-amber-50 border-b border-[#ECEAE4]">
          <span className="text-[9px] font-[800] uppercase tracking-[0.5px] text-amber-700 flex items-center gap-1">
            <Crown size={10} /> GROWTH
          </span>
        </div>
        {PLAN_FEATURES.builder.map((f, i) => (
          <FeatureRow key={`builder-${i}`} label={isEs ? f.es : f.en} starter={f.starter} pro={f.pro} builder={f.builder} />
        ))}
          </div>{/* /min-w-[520px] — closes F2 mobile scroll constraint */}
        </div>{/* /overflow-x-auto — closes F2 scroll wrapper */}
      </div>

      {/* ── ENTERPRISE ── */}
      <div className="bg-gradient-to-br from-[#1C1A17] to-[#374151] rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-3">
          <Building2 size={24} />
          <div>
            <h3 className="text-[16px] font-[900]">Enterprise</h3>
            <p className="text-[11px] font-[600] text-white/60">
              {isEs ? 'Para builders con 10+ proyectos simultáneos' : 'For builders with 10+ simultaneous projects'}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          {[
            { en: 'Unlimited Projects', es: 'Proyectos Ilimitados' },
            { en: 'Unlimited Users', es: 'Usuarios Ilimitados' },
            { en: 'Dedicated Support', es: 'Soporte Dedicado' },
            { en: 'Custom Integrations', es: 'Integraciones Custom' },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[11px] font-[600] text-white/80">
              <Check size={12} className="text-[#22C55E]" /> {isEs ? f.es : f.en}
            </div>
          ))}
        </div>
        {!showEnterprise ? (
          <button onClick={() => setShowEnterprise(true)}
            className="bg-white text-[#1C1A17] text-[12px] font-[800] px-6 py-2.5 rounded-full hover:bg-gray-100 transition-colors">
            {isEs ? 'Contactar Ventas' : 'Contact Sales'}
          </button>
        ) : (
          <EnterpriseForm isEs={isEs} onClose={() => setShowEnterprise(false)} />
        )}
      </div>

      {/* ── TROUBLESHOOTING (PR-NEW) ──
          Owner-scoped billing diagnostic. Calls the existing
          GET /api/stripe/diagnose route with the same Bearer
          pattern used elsewhere in this module. The response is
          non-leaky by construction (PR #95): env vars surface
          as 'configured'/'missing', Stripe IDs as booleans only,
          unmapped priceIds as last-6-char fragments. Renders
          unconditionally so the user can self-serve diagnostics
          without waiting for a polling timeout. */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl p-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <HelpCircle size={14} className="text-[#9E9890]" />
              <h3 className="text-[13px] font-[800]">
                {isEs ? 'Diagnóstico de pago' : 'Billing diagnostics'}
              </h3>
            </div>
            <p className="text-[11px] font-[600] text-[#9E9890]">
              {isEs
                ? 'Si tu plan no se actualizó después del pago, ejecuta el diagnóstico para revisar la configuración de Stripe.'
                : 'If your plan did not update after payment, run the diagnostic to inspect the Stripe configuration.'}
            </p>
          </div>
          <button
            type="button"
            onClick={runDiagnose}
            disabled={diagnoseStatus === 'loading'}
            className="shrink-0 text-[11px] font-[800] text-[#06539B] border border-[#06539B] px-3 py-1.5 rounded-full hover:bg-[#EFF6FF] disabled:opacity-40 transition-colors"
          >
            {diagnoseStatus === 'loading'
              ? (isEs ? 'Cargando…' : 'Loading…')
              : (isEs ? 'Diagnosticar pago' : 'Diagnose billing')}
          </button>
        </div>

        {diagnoseStatus === 'unauthorized' && (
          <div className="text-[11px] font-[700] text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {isEs
              ? 'Vuelve a iniciar sesión e intenta de nuevo.'
              : 'Please sign in again and retry.'}
          </div>
        )}
        {diagnoseStatus === 'error' && (
          <div className="text-[11px] font-[700] text-red-600 bg-red-50 rounded-lg px-3 py-2">
            {isEs
              ? 'No se pudo ejecutar el diagnóstico. Inténtalo de nuevo en unos segundos.'
              : 'Could not run the diagnostic. Try again in a few seconds.'}
          </div>
        )}

        {diagnose && diagnoseStatus === 'ok' && (
          <div className="space-y-3 text-[11px] font-[600]">
            {/* Conditional warnings — surface the most common
                operator misconfigurations as plain-language
                callouts before showing the raw flag table. */}
            {diagnose.envFlags.STRIPE_WEBHOOK_SECRET === 'missing' && (
              <div className="text-[11px] font-[700] text-red-600 bg-red-50 rounded-lg px-3 py-2">
                {isEs
                  ? 'Falta el Stripe webhook secret en Railway.'
                  : 'Stripe webhook secret is missing in Railway.'}
              </div>
            )}
            {diagnose.liveSubscription &&
              diagnose.liveSubscription.unmappedPriceIdCount > 0 && (
              <div className="text-[11px] font-[700] text-[#B45309] bg-[#FFFBEB] rounded-lg px-3 py-2">
                {isEs
                  ? 'El Price ID de Stripe no coincide con los precios configurados del plan.'
                  : 'Stripe price ID does not match the configured plan prices.'}
              </div>
            )}
            {checkoutResult === 'still-processing' &&
              !diagnose.user.stripeSubscriptionLinked && (
              <div className="text-[11px] font-[700] text-[#B45309] bg-[#FFFBEB] rounded-lg px-3 py-2">
                {isEs
                  ? 'El checkout terminó, pero todavía no hay suscripción de Stripe ligada a esta cuenta.'
                  : 'Checkout completed but no Stripe subscription is linked to this account yet.'}
              </div>
            )}

            {/* Multi-active warning — surfaces the BUILDER → PRO
                double-checkout case where the customer ended up
                with more than one active Stripe subscription. The
                repair route refuses to pick blindly; the operator
                must clean up duplicates in Stripe Dashboard first. */}
            {diagnose.customerSubscriptionSummary?.possibleMultipleActiveSubscriptions && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 space-y-1">
                <div className="text-[11px] font-[700] text-red-700">
                  {isEs
                    ? 'Se encontraron múltiples suscripciones en Stripe. Revisa Stripe Dashboard antes de reparar.'
                    : 'Multiple Stripe subscriptions found. Review Stripe Dashboard before repairing.'}
                </div>
                <div className="text-[10px] font-[600] text-red-700">
                  {isEs ? 'Activas: ' : 'Active: '}
                  {diagnose.customerSubscriptionSummary.activeSubscriptionCount}
                  {' · '}
                  {isEs ? 'En prueba: ' : 'Trialing: '}
                  {diagnose.customerSubscriptionSummary.trialingSubscriptionCount}
                </div>
              </div>
            )}

            {/* Self-serve recovery for the "disconnected state":
                customer is linked, no subscription is linked, AND
                customerSubscriptionSummary marks the case as
                recoverable (exactly one active/trialing sub that
                maps to a known plan). When more than one active
                sub exists, the warning above shows instead. */}
            {diagnose.user.stripeCustomerLinked &&
              !diagnose.user.stripeSubscriptionLinked &&
              diagnose.customerSubscriptionSummary?.hasRecoverableSubscription === true && (
              <div className="rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 space-y-2">
                <div className="text-[11px] font-[700] text-[#B45309]">
                  {isEs
                    ? 'Encontramos una suscripción activa en Stripe que no está ligada a tu cuenta.'
                    : 'An active Stripe subscription was found but is not linked to your account.'}
                </div>
                <button
                  onClick={runRepair}
                  disabled={repairStatus === 'loading'}
                  className="text-[11px] font-[800] px-3 py-1.5 rounded-md bg-[#1C1A17] text-white disabled:opacity-60"
                >
                  {repairStatus === 'loading'
                    ? (isEs ? 'Vinculando…' : 'Linking…')
                    : (isEs ? 'Reparar vínculo de suscripción' : 'Repair subscription link')}
                </button>
                {repairStatus === 'ok' && (
                  <div className="text-[11px] font-[700] text-[#15803D]">
                    {isEs ? 'Suscripción vinculada.' : 'Subscription linked.'}
                  </div>
                )}
                {repairStatus === 'not_found' && (
                  <div className="text-[11px] font-[700] text-[#B45309]">
                    {isEs
                      ? 'No se encontró una suscripción para este cliente en Stripe.'
                      : 'No subscription was found for this customer in Stripe.'}
                  </div>
                )}
                {repairStatus === 'already_linked' && (
                  <div className="text-[11px] font-[700] text-[#9E9890]">
                    {isEs
                      ? 'La suscripción ya está vinculada.'
                      : 'Subscription is already linked.'}
                  </div>
                )}
                {repairStatus === 'multiple_active' && (
                  <div className="text-[11px] font-[700] text-red-700">
                    {isEs
                      ? 'Se encontraron múltiples suscripciones activas. Cancela las duplicadas en Stripe Dashboard antes de reparar.'
                      : 'Multiple active subscriptions found. Cancel duplicates in Stripe Dashboard before repairing.'}
                  </div>
                )}
                {repairStatus === 'unauthorized' && (
                  <div className="text-[11px] font-[700] text-red-600">
                    {isEs
                      ? 'No autorizado. Cierra sesión y vuelve a entrar.'
                      : 'Not authorized. Sign out and back in.'}
                  </div>
                )}
                {repairStatus === 'error' && (
                  <div className="text-[11px] font-[700] text-red-600">
                    {isEs
                      ? 'No se pudo vincular. Intenta de nuevo en unos segundos.'
                      : 'Could not link. Try again in a few seconds.'}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
              <DiagLine label="STRIPE_SECRET_KEY" value={diagnose.envFlags.STRIPE_SECRET_KEY} />
              <DiagLine
                label={isEs ? 'Modo de Stripe' : 'Stripe mode'}
                value={diagnose.envFlags.stripeSecretMode}
              />
              <DiagLine label="STRIPE_WEBHOOK_SECRET" value={diagnose.envFlags.STRIPE_WEBHOOK_SECRET} />
              <DiagLine label="STRIPE_PRICE_PRO_MONTHLY" value={diagnose.envFlags.STRIPE_PRICE_PRO_MONTHLY} />
              <DiagLine label="STRIPE_PRICE_PRO_YEARLY" value={diagnose.envFlags.STRIPE_PRICE_PRO_YEARLY} />
              <DiagLine label="STRIPE_PRICE_BUILDER_MONTHLY" value={diagnose.envFlags.STRIPE_PRICE_BUILDER_MONTHLY} />
              <DiagLine label="STRIPE_PRICE_BUILDER_YEARLY" value={diagnose.envFlags.STRIPE_PRICE_BUILDER_YEARLY} />
              <DiagLine label="STRIPE_PRICE_STARTER_MONTHLY" value={diagnose.envFlags.STRIPE_PRICE_STARTER_MONTHLY} />
              <DiagLine label="STRIPE_PRICE_STARTER_YEARLY" value={diagnose.envFlags.STRIPE_PRICE_STARTER_YEARLY} />
              <DiagLine label="FRONTEND_URL" value={diagnose.envFlags.FRONTEND_URL} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 pt-2 border-t border-[#ECEAE4]">
              <DiagLine
                label={isEs ? 'Cliente de Stripe ligado' : 'Stripe customer linked'}
                value={diagnose.user.stripeCustomerLinked ? (isEs ? 'sí' : 'yes') : 'no'}
              />
              <DiagLine
                label={isEs ? 'Suscripción de Stripe ligada' : 'Stripe subscription linked'}
                value={diagnose.user.stripeSubscriptionLinked ? (isEs ? 'sí' : 'yes') : 'no'}
              />
              <DiagLine label={isEs ? 'Plan actual' : 'Current plan'} value={diagnose.user.plan} />
              <DiagLine label={isEs ? 'Plan efectivo' : 'Effective plan'} value={diagnose.user.effectivePlan} />
              <DiagLine
                label={isEs ? 'Estado de la suscripción' : 'Subscription status'}
                value={diagnose.user.subscriptionStatus}
              />
            </div>

            {diagnose.liveSubscription && (
              <div className="pt-2 border-t border-[#ECEAE4] space-y-1">
                <div className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.4px]">
                  {isEs ? 'Suscripción en vivo de Stripe' : 'Live Stripe subscription'}
                </div>
                {diagnose.liveSubscription.source === 'customer_fallback' && (
                  <DiagLine
                    label={isEs ? 'Fuente' : 'Source'}
                    value={isEs ? 'búsqueda por cliente' : 'customer lookup'}
                  />
                )}
                <DiagLine
                  label={isEs ? 'Estado' : 'Status'}
                  value={diagnose.liveSubscription.status}
                />
                <DiagLine
                  label={isEs ? 'Planes mapeados' : 'Mapped plans'}
                  value={
                    diagnose.liveSubscription.mappedPlans.length > 0
                      ? diagnose.liveSubscription.mappedPlans.join(', ')
                      : (isEs ? '(ninguno)' : '(none)')
                  }
                />
                <DiagLine
                  label={isEs ? 'Price IDs sin mapear' : 'Unmapped price IDs'}
                  value={String(diagnose.liveSubscription.unmappedPriceIdCount)}
                />
                {diagnose.liveSubscription.unmappedPriceIdsTruncated.length > 0 && (
                  <div className="text-[10px] font-[600] text-[#9E9890] pl-1">
                    {diagnose.liveSubscription.unmappedPriceIdsTruncated.join(' · ')}
                  </div>
                )}
              </div>
            )}

            {diagnose.customerSubscriptionSummary && (
              <div className="pt-2 border-t border-[#ECEAE4] space-y-1">
                <div className="text-[10px] font-[800] text-[#9E9890] uppercase tracking-[0.4px]">
                  {isEs ? 'Resumen de suscripciones del cliente' : 'Customer subscription summary'}
                </div>
                <DiagLine
                  label={isEs ? 'Total' : 'Total'}
                  value={String(diagnose.customerSubscriptionSummary.customerSubscriptionCount)}
                />
                <DiagLine
                  label={isEs ? 'Activas' : 'Active'}
                  value={String(diagnose.customerSubscriptionSummary.activeSubscriptionCount)}
                />
                <DiagLine
                  label={isEs ? 'En prueba' : 'Trialing'}
                  value={String(diagnose.customerSubscriptionSummary.trialingSubscriptionCount)}
                />
                <DiagLine
                  label={isEs ? 'Recuperable' : 'Recoverable'}
                  value={
                    diagnose.customerSubscriptionSummary.hasRecoverableSubscription
                      ? (isEs ? 'sí' : 'yes')
                      : 'no'
                  }
                />
                <DiagLine
                  label={isEs ? 'Múltiples activas' : 'Multiple active'}
                  value={
                    diagnose.customerSubscriptionSummary.possibleMultipleActiveSubscriptions
                      ? (isEs ? 'sí' : 'yes')
                      : 'no'
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── FAQ ── */}
      <div className="bg-white border border-[#ECEAE4] rounded-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[#ECEAE4] flex items-center gap-2">
          <HelpCircle size={16} className="text-[#9E9890]" />
          <h3 className="text-[14px] font-[900]">{isEs ? 'Preguntas Frecuentes' : 'FAQ'}</h3>
        </div>
        <div className="divide-y divide-[#F2F0EB]">
          {FAQ_ITEMS.map((faq, i) => (
            <div key={i}>
              <button
                onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-[#FAFAF8] transition-colors"
              >
                <span className="text-[12px] font-[700] text-[#1C1A17] pr-4">{isEs ? faq.es : faq.en}</span>
                {expandedFaq === i
                  ? <ChevronUp size={16} className="text-[#9E9890] flex-shrink-0" />
                  : <ChevronDown size={16} className="text-[#9E9890] flex-shrink-0" />
                }
              </button>
              {expandedFaq === i && (
                <div className="px-5 pb-4 text-[11px] font-[500] text-[#9E9890] leading-[1.7]">
                  {isEs ? faq.answerEs : faq.answerEn}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── PAUSE ──
          Billing-honesty fix: only render the Pause card when the user
          actually has a linked Stripe subscription. The previous gate
          (`!paused && !trial.active`) was true for every non-paused,
          non-trial user — including users who had never subscribed —
          and clicking "Pausar" would silently flip
          `subscriptionStatus = 'PAUSED'` on the DB row, creating a
          fake paused state for a non-existent subscription. */}
      {planSource?.stripeSubscriptionLinked === true && !plan.paused && !plan.trial?.active && (
        <div className="bg-white border border-[#ECEAE4] rounded-xl p-4" data-testid="subscription-pause-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Pause size={14} className="text-[#9E9890]" />
                <h3 className="text-[13px] font-[800]">{isEs ? 'Pausar Suscripción' : 'Pause Subscription'}</h3>
              </div>
              <p className="text-[11px] font-[600] text-[#9E9890]">
                {isEs
                  ? '¿Entre proyectos? Pausa por $5/mes. Tu información está segura. Reanuda cuando quieras.'
                  : 'Between projects? Pause for $5/mo. Your data stays safe. Resume anytime.'}
              </p>
            </div>
            <button onClick={() => pauseSub.mutate()} disabled={pauseSub.isPending}
              className="text-[10px] font-[800] text-[#9E9890] border border-[#ECEAE4] px-4 py-2 rounded-full hover:border-[#06539B] hover:text-[#1C1A17] transition-colors">
              {pauseSub.isPending ? '...' : (isEs ? 'Pausar' : 'Pause')}
            </button>
          </div>
        </div>
      )}

      {/* ── REFERRALS ── */}
      <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Gift size={16} className="text-[#EC4899]" />
          <h3 className="text-[13px] font-[800]">{isEs ? 'Referidos' : 'Referrals'}</h3>
        </div>
        <p className="text-[11px] font-[600] text-[#9E9890] mb-3">
          {isEs ? 'Invita a un builder, ambos reciben 1 mes gratis' : 'Invite a builder, both get 1 free month'}
        </p>
        {(plan.referrals?.freeMonthsEarned ?? 0) > 0 && (
          <div className="text-[12px] font-[800] text-[#22C55E] mb-2">
            {plan.referrals?.freeMonthsEarned ?? 0} {isEs ? 'meses gratis ganados' : 'free months earned'}
          </div>
        )}
        <button onClick={() => generateReferral.mutate()} disabled={generateReferral.isPending}
          className="w-full bg-gradient-to-r from-[#EC4899] to-[#06539B] text-white text-[13px] font-[800] py-2.5 rounded-full">
          {generateReferral.isPending ? '...' : (isEs ? 'Generar Código de Referido' : 'Generate Referral Code')}
        </button>
      </div>

      {/* ── TOTAL ──
          Billing-honesty fix: branch on `stripeSubscriptionLinked`.
          Linked subscribers keep the existing "Monthly Total" card
          (real billing summary sourced from `sub.billing`).
          Non-subscribers see an honest "No active billing" card
          instead — the previous card always read "$39/mo" for
          STARTER users regardless of whether they had ever paid,
          which falsely implied an active monthly bill. The BE
          response (`billing.totalMonthly = limits.priceMonthly + …`)
          is unchanged; the FE just stops rendering tier list-price
          as if it were a current bill for un-linked users. */}
      {planSource?.stripeSubscriptionLinked === true ? (
        <div className="bg-[#06539B] rounded-xl p-4 text-white" data-testid="subscription-monthly-total-linked">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-white/60 mb-1">{isEs ? 'Total Mensual' : 'Monthly Total'}</div>
              <div className="text-[28px] font-[900] tracking-[-1px]">
                ${sub?.billing?.totalMonthly ?? displayPriceMonthly}/mo
              </div>
              <div className="text-[10px] font-[600] text-white/60 mt-1">
                {plan.paused ? (isEs ? 'Pausado — $5/mes. Reanuda cuando quieras.' : 'Paused — $5/mo. Resume anytime.') : (
                  <>
                    ${sub?.billing?.planBase ?? displayPriceMonthly} {isEs ? 'base' : 'base'}
                    {(sub?.projects?.extraPurchased ?? 0) > 0 && ` + $${sub?.billing?.extraProjectsCost ?? 0} ${isEs ? 'extras' : 'extras'}`}
                    {(sub?.referrals?.freeMonthsEarned ?? 0) > 0 && ` · ${sub?.referrals?.freeMonthsEarned ?? 0} ${isEs ? 'meses gratis' : 'free months'}`}
                  </>
                )}
              </div>
            </div>
            {/* Billing-honesty fix: only render "Manage Billing" when the
                user actually has a Stripe customer on file. The previous
                gate was just `stripePromise` (a build-time presence check
                for VITE_STRIPE_PUBLISHABLE_KEY), which surfaced a dead
                button for every signed-in user — clicking it always
                returned a 400 from /api/stripe/portal because no
                `stripeCustomerId` existed. */}
            {stripePromise && planSource?.stripeCustomerLinked === true && (
              <button
                onClick={() => stripePortal.mutate()}
                disabled={stripePortal.isPending}
                className="flex items-center gap-1.5 bg-white text-[#1C1A17] text-[13px] font-[800] px-4 py-2.5 rounded-full hover:bg-gray-100 transition-colors"
                data-testid="subscription-manage-billing-button"
              >
                <CreditCard size={14} />
                {stripePortal.isPending ? '...' : (isEs ? 'Gestionar Pagos' : 'Manage Billing')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div
          className="bg-white border border-[#ECEAE4] rounded-xl p-4"
          data-testid="subscription-no-active-billing-card"
        >
          <div className="text-[9px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] mb-1">
            {isEs ? 'Sin facturación activa' : 'No active billing'}
          </div>
          <p className="text-[12px] font-[600] text-[#1C1A17] leading-relaxed">
            {isEs
              ? 'Actualmente estás en el plan STARTER. Suscríbete a un plan de pago arriba para desbloquear funciones PRO o BUILDER. Los detalles de facturación aparecerán aquí después de suscribirte.'
              : 'You are currently on the STARTER tier. Subscribe to a paid plan above to unlock PRO or BUILDER features. Billing details will appear here after you subscribe.'}
          </p>
        </div>
      )}
    </div>
  )
}

// ── Feature comparison row ────────────────────────

function FeatureRow({ label, starter, pro, builder }: {
  label: string; starter: boolean; pro: boolean; builder: boolean
}) {
  return (
    <div className="grid grid-cols-4 border-b border-[#F2F0EB] hover:bg-[#FAFAF8] transition-colors">
      <div className="px-4 py-2.5 text-[11px] font-[600] text-[#1C1A17]">{label}</div>
      {[starter, pro, builder].map((has, i) => (
        <div key={i} className={`px-4 py-2.5 flex items-center justify-center ${i < 2 ? 'border-r border-[#F2F0EB]' : ''}`}>
          {has ? (
            <Check size={14} className="text-[#22C55E]" />
          ) : (
            <Lock size={12} className="text-[#ECEAE4]" />
          )}
        </div>
      ))}
    </div>
  )
}

// ── Enterprise contact form ───────────────────────

function EnterpriseForm({ isEs, onClose }: { isEs: boolean; onClose: () => void }) {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', projects: '', message: '' })
  const [sent, setSent] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // In production: POST to /api/enterprise-inquiry
    setSent(true)
  }

  if (sent) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
        <Check size={24} className="text-[#22C55E] mx-auto mb-2" />
        <div className="text-[14px] font-[800]">{isEs ? '¡Recibido!' : 'Received!'}</div>
        <p className="text-[11px] font-[600] text-white/70 mt-1">
          {isEs ? 'Nuestro equipo te contactará en 24 horas.' : 'Our team will contact you within 24 hours.'}
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 space-y-3 mt-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[12px] font-[800]">{isEs ? 'Solicitar información' : 'Request info'}</span>
        <button type="button" onClick={onClose}><X size={16} className="text-white/50" /></button>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <input type="text" placeholder={isEs ? 'Nombre' : 'Name'} required value={form.name}
          onChange={e => setForm({ ...form, name: e.target.value })}
          className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-[11px] text-white placeholder-white/40 focus:outline-none focus:border-white/40" />
        <input type="text" placeholder={isEs ? 'Empresa' : 'Company'} required value={form.company}
          onChange={e => setForm({ ...form, company: e.target.value })}
          className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-[11px] text-white placeholder-white/40 focus:outline-none focus:border-white/40" />
        <input type="email" placeholder="Email" required value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
          className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-[11px] text-white placeholder-white/40 focus:outline-none focus:border-white/40" />
        <input type="tel" placeholder={isEs ? 'Teléfono' : 'Phone'} value={form.phone}
          onChange={e => setForm({ ...form, phone: e.target.value })}
          className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-[11px] text-white placeholder-white/40 focus:outline-none focus:border-white/40" />
      </div>
      <input type="text" placeholder={isEs ? '¿Cuántos proyectos simultáneos?' : 'How many simultaneous projects?'}
        value={form.projects} onChange={e => setForm({ ...form, projects: e.target.value })}
        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-[11px] text-white placeholder-white/40 focus:outline-none focus:border-white/40" />
      <textarea placeholder={isEs ? 'Cuéntanos sobre tu operación...' : 'Tell us about your operation...'}
        value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={2}
        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-[11px] text-white placeholder-white/40 focus:outline-none focus:border-white/40 resize-none" />
      <button type="submit"
        className="w-full flex items-center justify-center gap-2 bg-white text-[#1C1A17] text-[12px] font-[800] py-2.5 rounded-full hover:bg-gray-100 transition-colors">
        <Send size={14} /> {isEs ? 'Enviar solicitud' : 'Submit request'}
      </button>
    </form>
  )
}

// PR-NEW: presentational helper for the billing-diagnostic
// panel. Renders a label/value pair as a compact key:value row.
// The value is always converted via String() so an accidental
// non-string never crashes the page — important because the
// server response is treated as data, not as a typed contract.
function DiagLine({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="flex items-center justify-between gap-2 text-[11px]">
      <span className="text-[#9E9890] truncate">{label}</span>
      <span className="font-[800] text-[#1C1A17] truncate text-right">
        {String(value)}
      </span>
    </div>
  )
}
